import { computed, onMounted, reactive, toRefs } from 'vue';

export function usePaymentForm() {
  const organization_address = $('#organization-address').data('address');

  const state = reactive({
    achAccountName: null,
    achAccountType: null,
    achToken: null,
    bridgeTerms: false,
    country_selected: null,
    address1: organization_address?.street1 ?? "",
    city: organization_address?.city ?? "",
    state: organization_address?.state ?? "",
    zip: organization_address?.zip ?? "",
    poNumber: null,
    Notes: null,
    ccToken: null,
    ccExp: null,
    cvv: null,
    paymentType: null,
    appliedCredit: 0,
    creditAmount: null,
    billing_address: true,
    extra_fee: 0,
    country: "usa",
    invoiceOnlyMode: false,
    invoiceEnabled: false,
    selectedCreditCardId: 0,
    selectedAchId: 0,    
  });

  const ONLY_CREDITS_PAYMENT_TYPE = "accrued_credits";

  onMounted(() => {
    window.addEventListener('message', handleTokenizingResponse);
    setCountryAndState();
    deselectAllBillingTabs();
  });

  const setCountryAndState = () => {
    state.country = $("#country").val();
    state.country_selected = state.country;
    if (state.country == "puerto_rico") {
      state.state = "PR";
    }
  };

  const onCountryChange = () => {
    state.state = '';
    if (state.country_selected === 'puerto_rico') state.state = 'PR';
  };

  const handleTokenizingResponse = (event) => {
    try {
      var payload = JSON.parse(event.data);
      if (payload.hasOwnProperty('ccToken')) {
        console.log('Received Token (cc)');
        state.ccToken = payload.ccToken;
        document.getElementById('credit_card_token').value = state.ccToken;
      } else if (payload.hasOwnProperty('achToken')) {
        console.log('Received Token (ach)');
        state.achToken = payload.achToken;
        document.getElementById('ach_token').value = state.achToken;
      }
    } catch (e) {
      return false;
    }
  };

  const zipRegex = () => {
    if (state.country_selected == "canada") {
      return /^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/;
    }
    return /(^\d{5}$)|(^\d{5}-\d{4}$)/;
  };

  const payingWithCredit = computed(() => state.paymentType == 'credit');
  const payingWithACH = computed(() => state.paymentType == 'ach');

  const isCanadianVersion = computed(() => state.country == "canada");

  const setNewPaymentMethod = (newMethod) => {
    state.paymentType = newMethod;
  };

  const selectCreditCard = () => {
    setNewPaymentMethod('credit');
    state.billing_address = true;
  };

  const selectACHAccount = () => {
    setNewPaymentMethod('ach');
    state.billing_address = true;
  };

  const availableCredits = () => {
    var currentCredits = $("#total-credits").data('creditsAvailable');
    return currentCredits - state.appliedCredit;
  };

  const creditsCoverTotalCost = () => {
    return state.appliedCredit > 0 && calculateTotalWithoutCreditCardFee() < 0.01;
  };

  const total_charges_with_fees = () => {
    return Number($("#total_charges_with_fees").val());
  };

  const totalPaid = () => {
    let creditsAppliedBefore = Number($("#credits_applied_before").val());
    let cashPayment = Number($("#paid_amount").val());
    return creditsAppliedBefore + cashPayment;
  };

  const calculateCreditCardFee = () => {
    let percent_fee = Number($("#credit_card_percentage_fee").val());
    state.extra_fee = calculateTotalWithoutCreditCardFee() * (percent_fee / 100);
    return state.extra_fee;
  };

  const calculateTotalWithoutCreditCardFee = () => {
    return totalPurchased() - totalPaid() - state.appliedCredit - totalDiscounts();
  };

  const totalPurchased = () => {
    return total_charges_with_fees() + Number($("#total_credit_purchases").val());
  };

  const totalDiscounts = () => {
    return Number($("#total_discounts").val());
  };

  const calculateTotalWithCreditCardFee = () => {
    return calculateTotalWithoutCreditCardFee() + calculateCreditCardFee();
  };

  const termsFieldExists = () => {
    return document.querySelector('input#payment_terms_and_conditions') !== null;
  }

  const baseDataInvalid = () => {
    return termsArentSigned() || addressInvalid();
  };

  const termsArentSigned = () => {
    return (!state.bridgeTerms && termsFieldExists());
  };

  const addressInvalid = () => {
    return !state.address1 ||
           !state.city ||
           !state.state ||
           !(zipRegex().test(state.zip));
  };

  const achDataInvalid = () => {
    return isNewAchAccount.value ? (!state.achToken || !state.achAccountType || !state.achAccountName) : false;
  };

  // Credit card fields only required when "New Credit Card" is selected
  const ccDataInvalid = () => {
    return isNewCreditCard.value ? (!state.ccToken || !state.ccExp || !state.cvv) : false;
  };

  const baseValidationsFailed = () => {
    if (state.paymentType == null) {
      return true;
    }
    if(termsArentSigned()){
      return true;
    }
    if(state.billing_address == true && addressInvalid()){
      return true;
    }
    if (state.paymentType == 'credit') {
      return ccDataInvalid();
    } else if (state.paymentType == 'ach') {
      return achDataInvalid();
    }
    return false;
  }

  const deselectAllBillingTabs = () => {
    $('#billing-tabs .nav-link.active').removeClass('active').attr('aria-selected', 'false');
    $('#billing-tabs-content .tab-pane.active, #billing-tabs-content .tab-pane.show').removeClass('active show');
  };

  const hideBillingTabs = () => {
    deselectAllBillingTabs();
    $("#billing_methods_div").addClass("d-none");
  };

  const showBillingTabs = () => {
    $("#billing_methods_div").removeClass("d-none");
  };

  const deleteCredit = () => {
    state.appliedCredit = 0;
    if(state.paymentType == ONLY_CREDITS_PAYMENT_TYPE) {
      showBillingTabs();
      setNewPaymentMethod(null);
      state.billing_address = true;
    }
  };

  const addCredit = () => {
    if (unableToAddCredit()) return true;
    state.appliedCredit += Number(state.creditAmount);
    state.creditAmount = null;
    if (creditsCoverTotalCost()) {
      hideBillingTabs();
      state.billing_address = false;
      setNewPaymentMethod(ONLY_CREDITS_PAYMENT_TYPE);
    }
    calculateTotalWithCreditCardFee();
  };

  const unableToAddCredit = () => {
    if(isNaN(state.creditAmount) || Number(state.creditAmount) <= 0 || !isNumberUntilTwoDecimalPlaces(state.creditAmount)){
      return true;
    }

    let amount = Number(state.creditAmount);
    return amount > availableCredits() || amount + totalPaid() + state.appliedCredit > totalPurchased();
  };

  const isNumberUntilTwoDecimalPlaces = (value) => {
    let number = parseFloat((Number(value) * 100).toFixed(3));
    return Number.isSafeInteger(number);
  };

  const isNewCreditCard = computed(() => {
    return Number(state.selectedCreditCardId) === 0;
  });

  const isNewAchAccount = computed(() => {
    return Number(state.selectedAchId) === 0;
  });

  const onCreditCardSelectChange = () => {
    if (Number(state.selectedCreditCardId) !== 0) {
      state.ccToken = null;
      state.ccExp = null;
      state.cvv = null;
    }
  }

  const onAchSelectChange = () => {
    if (Number(state.selectedAchId) !== 0) {
      state.achToken = null;
      state.achAccountType = null;
      state.achAccountName = null;
    }
  }

  return {
    ...toRefs(state),
    state,
    handleTokenizingResponse,
    setCountryAndState,
    onCountryChange,
    zipRegex,
    payingWithCredit,
    payingWithACH,
    isCanadianVersion,
    setNewPaymentMethod,
    selectCreditCard,
    selectACHAccount,
    availableCredits,
    creditsCoverTotalCost,
    total_charges_with_fees,
    totalPaid,
    calculateCreditCardFee,
    calculateTotalWithoutCreditCardFee,
    calculateTotalWithCreditCardFee,
    termsFieldExists,
    baseDataInvalid,
    achDataInvalid,
    ccDataInvalid,
    totalPurchased,
    baseValidationsFailed,
    deselectAllBillingTabs,
    hideBillingTabs,
    showBillingTabs,
    deleteCredit,
    unableToAddCredit,
    addCredit,
    isNewCreditCard,
    onCreditCardSelectChange,
    onAchSelectChange,
    isNewAchAccount,
  };
}
